import i18nFactory from '@yandex-int/i18n';
import { get, uniqBy, uniqueId } from 'lodash';

import { GetPhrasesStatsResponsePhrase } from '../../api/RestApiManager';
import {
    PHRASES_GENERATOR_DIVIDERS,
    PHRASES_GENERATOR_EMPTY_PHRASE_SYMBOLS,
    PHRASES_GENERATOR_NEW_LINE_WITH_SPACES_REGEXP,
    PHRASES_SELECTOR_MAX_NESTED_CHILDREN,
    PHRASES_SELECTOR_SPACE_SYMBOLS_REGEXP
} from '../../constants';
import { phraseIdFieldName } from '../../declarations/filter/filter';
import { mapCampaignTypeToCalcType } from '../../mappers/filter/filter';
import { mapStringToPhrasesSelectorListItem } from '../../mappers/phrasesSelectorWizard';
import { GRID_PREFIX, KeyCode, phrases as phrasesView, ValidationResultItem } from '../../typings/common';
import { FilterExpressionValueType, Operator } from '../../typings/filter';
import { GridScreenUrlSearchQueryParams } from '../../typings/grid';
import {
    KeywordsStatDictionary,
    PhrasesSelectorGeneratorGroupPhrases,
    PhrasesSelectorGroupInfo,
    PhrasesSelectorListItem,
    PhrasesSelectorPhrase,
    PhrasesSelectorPhraseNodeStateMap,
    PhrasesSelectorStartPhraseFlattenedNode,
    PhrasesSelectorStartPhraseNode,
    PhrasesSelectorStartPhraseNodeState,
    PhrasesSelectorStartPhrases,
    PhrasesUpdateResult
} from '../../typings/phrases';
import { createErrorsMapper } from '../ErrorsMapper/ErrorsMapper';
import { serializeFilterToString } from '../FilterHelpers/FilterHelpers';
import { UrlHelpers } from '../UrlHelpers';
import { VirtualTreeHelpers } from '../VirtualTreeHelpers/VirtualTreeHelpers';

import * as keyset from './PhrasesSelectorWizard.i18n';

const i18n = i18nFactory(keyset);

export function mergeAndSortSelectorListItems(lists: PhrasesSelectorListItem[][]): PhrasesSelectorListItem[] {
    return uniqBy(lists.reduce((acc, el) => acc.concat(el), []), 'text').sort(
        (a: PhrasesSelectorListItem, b: PhrasesSelectorListItem) => b.shows - a.shows
    );
}

export function getKeywordStatPhrases(
    startPhrases: PhrasesSelectorListItem[],
    chosenPhrases: PhrasesSelectorListItem[],
    keywordsStat: KeywordsStatDictionary
): {
    similarPhrases: PhrasesSelectorListItem[];
    searchedAlsoPhrases: PhrasesSelectorListItem[];
} {
    const selectedStartPhrases = startPhrases.filter(el => el.selected);
    const chosenPhrasesSet = new Set<string>(chosenPhrases.map(el => el.text));

    const similarPhrases = mergeAndSortSelectorListItems(
        selectedStartPhrases.map(({ text }) =>
            keywordsStat[text] ? keywordsStat[text].searchedWith.filter(el => !chosenPhrasesSet.has(el.text)) : []
        )
    ).map(el => ({ ...el, selected: false }));

    const searchedAlsoPhrases = mergeAndSortSelectorListItems(
        selectedStartPhrases.map(({ text }) =>
            keywordsStat[text] ? keywordsStat[text].searchedAlso.filter(el => !chosenPhrasesSet.has(el.text)) : []
        )
    ).map(el => ({ ...el, selected: false }));

    return {
        similarPhrases,
        searchedAlsoPhrases
    };
}

export function getNewMinusWordsList(
    startPhrases: PhrasesSelectorListItem[],
    oldMinusWords: PhrasesSelectorListItem[],
    newMinusWords: PhrasesSelectorListItem[]
): PhrasesSelectorListItem[] {
    const excludedWords = new Set<string>([...oldMinusWords, ...startPhrases].map(el => el.text));

    const minusWords = newMinusWords
        .filter(el => !excludedWords.has(el.text))
        .map(el => mapStringToPhrasesSelectorListItem(el.text));

    return oldMinusWords.concat(minusWords);
}

export function getNewChosenWordsList(
    oldChosenPhrases: PhrasesSelectorListItem[],
    newChosenPhrases: PhrasesSelectorListItem[]
): PhrasesSelectorListItem[] {
    const excludedWords = new Set<string>(oldChosenPhrases.map(el => el.text));

    return [...oldChosenPhrases, ...newChosenPhrases.filter(el => !excludedWords.has(el.text))];
}

export function processSelection(
    items: PhrasesSelectorListItem[],
    indexOfClick: number,
    indexOfPreviousClick: number,
    keyCode?: KeyCode
): Array<boolean> {
    return items.map((el, index) => {
        switch (keyCode) {
            case KeyCode.CTRL:
                return indexOfClick === index ? !el.selected : el.selected;
            case KeyCode.SHIFT:
                if (indexOfPreviousClick <= indexOfClick) {
                    return index <= indexOfClick && index >= indexOfPreviousClick;
                } else {
                    return index >= indexOfClick && index <= indexOfPreviousClick;
                }
            default:
                return indexOfClick === index;
        }
    });
}

export function getPhrasesCount(phrases: PhrasesSelectorGeneratorGroupPhrases): number {
    return parsePhrases(replaceDividerSymbols(phrases)).length;
}

export function getGeneratedPhrasesCount(phrasesGroups: Array<PhrasesSelectorGeneratorGroupPhrases>): number {
    return phrasesGroups.map(getPhrasesCount).reduce((product, phrasesCount) => {
        if (product === 0) {
            return phrasesCount;
        }

        if (phrasesCount !== 0) {
            return product * phrasesCount;
        }

        return product;
    }, 0);
}

function parsePhrases(phrases: PhrasesSelectorGeneratorGroupPhrases): Array<string> {
    const result: Array<string> = [];

    return phrases.split('\n').reduce((parsedPhrases, phrase) => {
        const phraseWithoutWhitespace = phrase.replace(PHRASES_SELECTOR_SPACE_SYMBOLS_REGEXP, ' ').trim();

        if (phraseWithoutWhitespace) {
            parsedPhrases.push(phraseWithoutWhitespace);
        }

        return parsedPhrases;
    }, result);
}

export function replaceDividerSymbols(phrases: PhrasesSelectorGeneratorGroupPhrases): string {
    const reg = new RegExp(`[${PHRASES_GENERATOR_DIVIDERS}]`, 'g');

    return phrases.replace(reg, '\n').replace(PHRASES_GENERATOR_NEW_LINE_WITH_SPACES_REGEXP, '\n');
}

export function generatePhrases(phrasesGroups: Array<PhrasesSelectorGeneratorGroupPhrases>): Array<string> {
    function addTo(curr: Array<string>, args: Array<Array<string>>): Array<Array<string>> {
        let copy;
        const [head, ...tail] = args;
        const isLast = tail.length === 0;
        let result: Array<Array<string>> = [];

        for (let i = 0; i < head.length; i++) {
            copy = curr.slice();

            if (!PHRASES_GENERATOR_EMPTY_PHRASE_SYMBOLS.includes(head[i])) {
                copy.push(head[i]);
            }

            if (isLast) {
                result.push(copy);
            } else {
                result = [...result, ...addTo(copy, tail)];
            }
        }

        return result;
    }

    return addTo([], phrasesGroups.map(parsePhrases).filter(p => p.length !== 0)).map(phrases => phrases.join(' '));
}

export function getPhraseNodeDefaultState(): PhrasesSelectorStartPhraseNodeState {
    return {
        expanded: false,
        loading: false,
        selected: false,
        expandable: true
    };
}

function createPhraseNode(
    name: string,
    shows: number,
    children: Array<PhrasesSelectorStartPhraseNode>,
    state?: Partial<PhrasesSelectorStartPhraseNodeState>
): PhrasesSelectorStartPhraseNode {
    return {
        id: uniqueId('startPhrase'),
        name,
        info: {
            shows
        },
        state: {
            ...getPhraseNodeDefaultState(),
            ...state
        },
        children
    };
}

export function getPhraseNodeState(
    phrase: PhrasesSelectorStartPhraseNode | PhrasesSelectorStartPhraseFlattenedNode
): PhrasesSelectorStartPhraseNodeState {
    const { state = getPhraseNodeDefaultState() } = phrase;

    return state;
}

export function filterPhrasesWithoutShows(
    phrases: Array<PhrasesSelectorStartPhraseNode>
): Array<PhrasesSelectorStartPhraseNode> {
    return phrases.filter(phrase => phrase.info.shows !== 0);
}

function getSimilarPhraseChildren(
    phrasesStats: Array<GetPhrasesStatsResponsePhrase>,
    phraseIndex: number
): Array<PhrasesSelectorStartPhraseNode> {
    return phrasesStats[phraseIndex].searchedAlso.map(({ phrase: phraseText, shows }) =>
        createPhraseNode(phraseText, shows, [])
    );
}

function getNestedPhraseChildren(
    phraseStats: GetPhrasesStatsResponsePhrase,
    phrase: string
): Array<PhrasesSelectorStartPhraseNode> {
    const result: Array<PhrasesSelectorStartPhraseNode> = [];

    return phraseStats.searchedWith
        .slice(0, PHRASES_SELECTOR_MAX_NESTED_CHILDREN)
        .reduce((children, { phrase: phraseText, shows }) => {
            if (phraseText !== phrase) {
                children.push(createPhraseNode(phraseText, shows, []));
            }

            return children;
        }, result);
}

function sortPhrasesByShows(a: PhrasesSelectorStartPhraseNode, b: PhrasesSelectorStartPhraseNode): number {
    return b.info.shows - a.info.shows;
}

export function createNestedPhrasesTree(
    startPhrasesNames: Array<string>,
    nodesStateMap: PhrasesSelectorPhraseNodeStateMap,
    phrasesResponseMap: Record<string, GetPhrasesStatsResponsePhrase>,
    phrasesShowsMap: Record<string, number>
): Array<PhrasesSelectorStartPhraseNode> {
    const createChildren = (
        phrases: Array<string>,
        showsMap: Record<string, number>
    ): Array<PhrasesSelectorStartPhraseNode> => {
        return phrases.map(phrase => {
            if (!nodesStateMap.expanded[phrase]) {
                return createPhraseNode(phrase, showsMap[phrase], [], { selected: nodesStateMap.selected[phrase] });
            }

            const nestedChildren = getNestedPhraseChildren(phrasesResponseMap[phrase], phrase);

            return createPhraseNode(
                phrase,
                showsMap[phrase],
                createChildren(
                    nestedChildren.map(p => p.name),
                    nestedChildren.reduce((acc, p) => {
                        acc[p.name] = p.info.shows;
                        return acc;
                    }, {})
                ),
                {
                    expanded: nestedChildren.length !== 0,
                    selected: nodesStateMap.selected[phrase]
                }
            );
        });
    };

    return VirtualTreeHelpers.selectParents(createChildren(startPhrasesNames, phrasesShowsMap));
}

export function mapPhrasesStatsToClient(
    startPhrasesNames: Array<string>,
    queryPhrasesNames: Array<string>,
    responsePhrases: Array<GetPhrasesStatsResponsePhrase>,
    nodesStateMap: PhrasesSelectorPhraseNodeStateMap
): PhrasesSelectorStartPhrases {
    const phrasesResponseMap = queryPhrasesNames.reduce((acc, phrase, index) => {
        acc[phrase] = responsePhrases[index];
        return acc;
    }, {});
    const phrasesShowsMap = queryPhrasesNames.reduce((acc, phrase, index) => {
        acc[phrase] = responsePhrases[index].shows;
        return acc;
    }, {});

    const nestedPhrases = createNestedPhrasesTree(
        startPhrasesNames,
        nodesStateMap,
        phrasesResponseMap,
        phrasesShowsMap
    ).sort(sortPhrasesByShows);

    const similarPhrases = startPhrasesNames
        .reduce<Array<PhrasesSelectorStartPhraseNode>>((acc, phrase, index) => {
            acc.push(...getSimilarPhraseChildren(responsePhrases, index));

            return acc;
        }, [])
        .sort(sortPhrasesByShows);

    return {
        similar: similarPhrases,
        nested: nestedPhrases
    };
}

export function mapPhraseNestedStatsToClient(
    phrase: PhrasesSelectorStartPhraseNode,
    phrasesResponse: Array<GetPhrasesStatsResponsePhrase>,
    startNestedPhrases: Array<PhrasesSelectorStartPhraseNode>,
    excludeWithoutShows: boolean = false
): Array<PhrasesSelectorStartPhraseNode> {
    const [phraseStats] = phrasesResponse;
    let children = getNestedPhraseChildren(phraseStats, phrase.name).sort(sortPhrasesByShows);

    if (excludeWithoutShows) {
        children = filterPhrasesWithoutShows(children);
    }

    const newPhrase = {
        ...phrase,
        state: {
            ...(phrase.state || {}),
            loading: false,
            selected: false,
            expanded: true,
            expandable: Boolean(children && children.length !== 0)
        },
        children
    };

    return VirtualTreeHelpers.selectParents(VirtualTreeHelpers.replaceNode(startNestedPhrases, newPhrase));
}

export function createPhrasesLink({
    login,
    campaign,
    groupId,
    phrasesIds
}: {
    login: string;
    campaign: PhrasesSelectorGroupInfo['campaign'];
    groupId: string;
    phrasesIds: Array<string>;
}): string {
    const filter = serializeFilterToString([
        {
            field: phraseIdFieldName,
            operator: Operator.EQUALS,
            value: {
                type: FilterExpressionValueType.numberSet,
                value: phrasesIds
            }
        }
    ]);

    return UrlHelpers.createNewDirectLink(UrlHelpers.createNewDirectUrlPathByParams(GRID_PREFIX, phrasesView), {
        ulogin: login,
        filter,
        [GridScreenUrlSearchQueryParams.campaignsIds]: String(campaign.id),
        [GridScreenUrlSearchQueryParams.groupsIds]: String(groupId),
        [GridScreenUrlSearchQueryParams.calcType]: mapCampaignTypeToCalcType(campaign.type)
    });
}

export function expandStartPhrases(
    prevPhrases: Array<PhrasesSelectorStartPhraseNode>,
    nextPhrases: Array<PhrasesSelectorStartPhraseNode>
): Array<PhrasesSelectorStartPhraseNode> {
    const expandedPhrasesMap = prevPhrases.reduce((acc, node) => {
        if (node.state.expanded) {
            acc[node.name] = true;
        }

        return acc;
    }, {});

    return VirtualTreeHelpers.expandNodes(nextPhrases, expandedPhrasesMap);
}

export function removeKeywordsFromStartPhrases(
    startPhrases: Array<PhrasesSelectorStartPhraseNode>,
    keywords: Array<PhrasesSelectorPhrase>
): Array<PhrasesSelectorStartPhraseNode> {
    const keywordsSet = new Set(keywords.map(k => k.name));
    return VirtualTreeHelpers.removeNodes(startPhrases, node => keywordsSet.has(node.name));
}

export function getValidationError(validationError: ValidationResultItem): string {
    const errorMapper = createErrorsMapper<PhrasesUpdateResult>(
        ({ idx }) => [
            {
                path: ['addItems', idx],
                codes: [
                    {
                        code: 'CampaignDefectIds.Gen.ARCHIVED_CAMPAIGN_MODIFICATION',
                        message: (): string => i18n('archived-campaign-modification')
                    }
                ]
            },
            {
                path: ['addItems', idx],
                codes: [
                    {
                        code: 'KeywordDefectIds.Keyword.MAX_KEYWORDS_PER_AD_GROUP_EXCEEDED',
                        message: (err: ValidationResultItem): string =>
                            i18n('max-keywords-exceeded', {
                                maxKeywords: get(err, 'params.maxKeywords')
                            })
                    }
                ]
            }
        ],
        () => i18n('validation-error')
    );

    return String(errorMapper(validationError));
}

export function getGroupKeywordsMap(keywords: Array<string>): Record<string, boolean> {
    return keywords.reduce((acc, keyword) => {
        acc[keyword.toLowerCase()] = true;

        return acc;
    }, {});
}
